import { Component, Input, OnChanges, Output, EventEmitter, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MsgVO, GuestContactVO, ReservationVO, NoteRequestAPI, MsgOrigin } from '../unified-inbox.models';
import { UnifiedInboxService } from '../unified-inbox.service';
import { QuickTaskComponent } from '@app/_components/task/quick-task/quick-task.component';

@Component({
    selector: 'app-unified-thread',
    templateUrl: './unified-thread.component.html',
    styleUrls: ['./unified-thread.component.scss'],
    standalone: false
})
export class UnifiedThreadComponent implements OnChanges {
    @Input() conversationId: string;
    @Input() context: number;
    @Input() contact: GuestContactVO;
    @Input() reservations: ReservationVO[] = [];
    @Input() availableLabels: string[] = [];
    @Output() guestIdentified = new EventEmitter<{ name: string; email: string }>();
    @Output() labelToggled = new EventEmitter<string>();
    @Output() conversationArchived = new EventEmitter<string>();

    messages: MsgVO[] = [];
    /** Messages grouped by calendar day for the date-divider display. */
    messageGroups: { dateLabel: string; messages: MsgVO[] }[] = [];

    loading = false;
    loadError = '';

    summarizing = false;
    summary = '';
    summaryError = '';

    @ViewChild('composerEl') composerEl: ElementRef;

    addingNote = false;
    noteBody = '';
    replyMode = false;
    showLabelMenu = false;
    showOverflowMenu = false;
    isFlagged = false;

    archiveSuccess = false;
    archiveError = '';

    addTaskSuccess = false;
    addTaskError = '';

    noteError = '';
    bizDevSuccess = false;
    bizDevError = '';
    deleteError = '';

    constructor(
        private unifiedInboxService: UnifiedInboxService,
        private dialog: MatDialog,
        private router: Router
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['conversationId'] || changes['context']) {
            this.loadThread();
        }
    }

    loadThread(): void {
        if (!this.conversationId) {
            this.messages = [];
            return;
        }

        // Clear immediately so stale thread doesn't linger while loading.
        this.messages = [];
        this.loading = true;
        this.loadError = '';
        this.summarizing = false;
        this.summary = '';
        this.summaryError = '';

        this.unifiedInboxService.getThread(this.conversationId).subscribe({
            next: res => {
                this.loading = false;
                const raw = res?.data?.data?.messages;
                this.messages = Array.isArray(raw) ? this.mapMessages(raw) : [];
                this.buildMessageGroups(this.messages);
                this.emitGuestIdentity(raw || []);
            },
            error: err => {
                this.loading = false;
                this.loadError = err?.error?.message || 'Failed to load thread.';
                this.messages = [];
            }
        });
    }

    private emitGuestIdentity(rawList: any[]): void {
        // First try: find an incoming message — guest is the sender
        const incoming = rawList.find(m => !this.isOutgoing(m) && (m.senderEmail || m.senderName));
        if (incoming) {
            this.guestIdentified.emit({ name: incoming.senderName || '', email: incoming.senderEmail || '' });
            return;
        }
        // Fallback: all messages are outgoing KBM emails — guest is the recipient
        const outgoing = rawList.find(m => this.isOutgoing(m) && m.recipientEmail);
        if (outgoing) {
            this.guestIdentified.emit({ name: '', email: outgoing.recipientEmail });
        }
    }

    /** Returns true when a string value is an S3 key path rather than displayable content. */
    private isS3Key(value: string): boolean {
        return typeof value === 'string' && (value.startsWith('str_msg/') || value.startsWith('email/'));
    }

    private mapMessages(list: any[]): MsgVO[] {
        return list.map(m => {
            const origin: number = m.msgOrigin ?? m.origin ?? 0;
            const channel = this.originToChannel(origin);
            // mobileText is always raw content (SMS/note). emailText and emailHtml may be S3 key
            // paths for newer email messages — skip them when they are key paths so the bubble
            // does not show a raw storage path as the message content.
            const mobileBody = m.mobileText || '';
            const emailBody = (m.emailText && !this.isS3Key(m.emailText)) ? m.emailText : '';
            const body = mobileBody || emailBody || m.body || '';
            const rawEmailHtml = m.emailHtml || m.bodyHtml || '';
            const emailHtml = (rawEmailHtml && !this.isS3Key(rawEmailHtml)) ? rawEmailHtml : '';
            const dateVal = m.sentDate || m.createDate || m.receivedDate;
            return {
                msgId: m.msgId,
                conversationId: m.msgConversationId || m.conversationId,
                context: m.msgContext,
                origin: origin,
                channel: channel,
                senderName: m.senderName || '',
                senderEmail: m.senderEmail || '',
                senderMobileNum: m.senderMobileNum || '',
                recipientEmail: m.recipientEmail || '',
                recipientMobileNum: m.recipientMobileNum || '',
                subject: m.subject || '',
                body: body,
                bodyHtml: emailHtml,
                createDate: this.formatDate(dateVal),
                createDateRaw: dateVal ? String(dateVal) : '',
                sentBy: m.senderName || '',
                isIncoming: !this.isOutgoing(m),
                isNote: origin === MsgOrigin.INTERNAL_NOTE,
                isSystem: origin === MsgOrigin.VRBO_API,
                deliveryStatus: m.status || '',
                attachmentCount: m.attachmentCount || 0,
                mailbox: m.folder || '',
                label: null,
                expanded: false
            } as MsgVO;
        });
    }

    /**
     * Groups a flat message list by calendar day and builds `messageGroups`.
     * Label is "Today", "Yesterday", or "MMM D" (e.g. "May 22").
     */
    private buildMessageGroups(msgs: MsgVO[]): void {
        const now = new Date();
        const todayKey = this.dayKey(now);
        const yesterdayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        const yesterdayKey = this.dayKey(yesterdayDate);

        const groups: { dateLabel: string; messages: MsgVO[] }[] = [];
        let currentKey = '';

        for (const msg of msgs) {
            const d = msg.createDateRaw ? new Date(msg.createDateRaw) : null;
            const key = d && !isNaN(d.getTime()) ? this.dayKey(d) : 'unknown';
            if (key !== currentKey) {
                currentKey = key;
                let label: string;
                if (key === todayKey) {
                    label = 'Today';
                } else if (key === yesterdayKey) {
                    label = 'Yesterday';
                } else if (d && !isNaN(d.getTime())) {
                    label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                } else {
                    label = '';
                }
                groups.push({ dateLabel: label, messages: [] });
            }
            groups[groups.length - 1].messages.push(msg);
        }
        this.messageGroups = groups;
    }

    private dayKey(d: Date): string {
        return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    }

    private isOutgoing(m: any): boolean {
        const sender = (m.senderEmail || '').toLowerCase();
        if (sender.endsWith('@kbmhawaii.com') || sender.endsWith('@kbmresorts.com') || sender.endsWith('@kbm.com') || sender.endsWith('@kbmr.net')) {
            return true;
        }
        // Automated SMS (arrival agent, drip) have no senderEmail but use KBM's Twilio number.
        const senderMobile = (m.senderMobileNum || '').replace(/\D/g, '');
        if (senderMobile === '8084009160' || senderMobile === '18084009160') {
            return true;
        }
        return false;
    }

    private originToChannel(origin: number): string {
        switch (origin) {
            case MsgOrigin.SMS: return 'SMS';
            case MsgOrigin.CALL: return 'Call';
            case MsgOrigin.VRBO_API: return 'OTA';
            case MsgOrigin.GUEST_PORTAL: return 'Portal';
            case MsgOrigin.EMAIL: return 'Email';
            case MsgOrigin.INTERNAL_NOTE: return 'Note';
            case MsgOrigin.ADMIN_INBOX: return 'Email';
            default: return '';
        }
    }

    private formatDate(value: any): string {
        if (!value) { return ''; }
        const d = new Date(value);
        if (isNaN(d.getTime())) { return String(value); }
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    }

    get initials(): string {
        if (!this.contact) { return '?'; }
        const f = this.contact.firstName ? this.contact.firstName.charAt(0) : '';
        const l = this.contact.lastName ? this.contact.lastName.charAt(0) : '';
        return (f + l).toUpperCase() || '?';
    }

    onMessageSent(): void {
        this.loadThread();
    }

    summarize(): void {
        if (!this.conversationId || this.summarizing) { return; }
        this.summarizing = true;
        this.summary = '';
        this.summaryError = '';
        this.unifiedInboxService.summarizeThread(this.conversationId).subscribe({
            next: res => {
                this.summarizing = false;
                this.summary = res?.data?.data?.summary || '';
            },
            error: () => {
                this.summarizing = false;
                this.summaryError = 'Unable to summarize thread.';
            }
        });
    }

    openNotePanel(): void { this.addingNote = true; }
    closeNotePanel(): void { this.addingNote = false; this.noteBody = ''; }

    private buildTaskName(): string {
        // --- Guest last name segment ---
        let lastName = '';
        if (this.contact?.lastName?.trim()) {
            lastName = this.contact.lastName.trim();
        } else if (this.contact?.firstName?.trim()) {
            // contact populated with only first name (split from senderName in parent)
            const parts = this.contact.firstName.trim().split(/\s+/);
            if (parts.length > 1) {
                lastName = parts[parts.length - 1];
            }
        }
        if (!lastName) {
            // Try to extract last name from first incoming message senderName
            const incoming = this.messages.find(m => m.isIncoming && m.senderName);
            if (incoming?.senderName) {
                const parts = incoming.senderName.trim().split(/\s+/);
                if (parts.length > 1) { lastName = parts[parts.length - 1]; }
            }
        }

        // --- Reservation number segment ---
        let resNumber = '';
        const firstRes = (this.reservations || [])[0];
        if (firstRes?.confirmationNumber?.trim()) {
            resNumber = firstRes.confirmationNumber.trim();
        }

        // --- Issue/subject segment ---
        // Prefer the subject of the most recent inbound message, then any message subject.
        const subjectSource = [...this.messages].reverse().find(m => m.subject?.trim());
        let issue = subjectSource?.subject?.trim() || '';
        // Strip Re:/Fwd:/RE:/FW: prefixes (any combination, any case)
        issue = issue.replace(/^(Re:\s*|Fwd?:\s*|Fw:\s*)+/gi, '').trim();
        // Strip raw email thread path IDs like "email/15/2026-04-14-17-32-44-283_..."
        if (/^email\/\d+\//.test(issue)) { issue = ''; }

        // --- Assemble ---
        const parts: string[] = [];
        if (lastName) { parts.push(lastName); }
        if (resNumber) { parts.push(resNumber); }

        const prefix = parts.join(' ');
        let name: string;

        if (prefix && issue) {
            name = `${prefix} — ${issue}`;
        } else if (prefix) {
            name = prefix;
        } else if (issue) {
            name = `Guest inquiry — ${issue}`;
        } else {
            name = 'Guest inquiry';
        }

        // Truncate to 100 chars
        return name.length > 100 ? name.substring(0, 97) + '...' : name;
    }

    openAddTaskDialog(): void {
        this.addTaskSuccess = false;
        this.addTaskError = '';

        const taskName = this.buildTaskName();
        const prefillTask = taskName ? { name: taskName } : null;

        const config = new MatDialogConfig();
        config.width = '600px';
        config.maxHeight = '90vh';
        config.disableClose = false;
        config.autoFocus = true;
        config.hasBackdrop = true;
        config.data = {
            title: 'Add Task',
            isModal: true,
            task: prefillTask
        };

        const dialogRef = this.dialog.open(QuickTaskComponent, config);

        // afterClosed emits the value passed to dialogRef.close(task) — a non-null task
        // means the save succeeded. This is the canonical Material dialog success pattern and
        // avoids timing issues with componentInstance @Output subscription.
        dialogRef.afterClosed().subscribe((savedTask: any) => {
            if (savedTask) {
                this.addTaskSuccess = true;
                this.addTaskError = '';
                // Auto-hide success banner after 4 seconds
                setTimeout(() => { this.addTaskSuccess = false; }, 4000);
            }
        });
    }

    submitNote(): void {
        if (!this.noteBody.trim()) { return; }
        this.noteError = '';
        const req: NoteRequestAPI = {
            msgConversationId: this.conversationId,
            body: this.noteBody.trim(),
            msgContext: this.context
        };
        this.unifiedInboxService.postNote(req).subscribe({
            next: () => { this.closeNotePanel(); this.loadThread(); },
            error: () => {
                this.noteError = 'Failed to post note.';
                setTimeout(() => { this.noteError = ''; }, 4000);
            }
        });
    }

    openReply(): void {
        this.replyMode = true;
        setTimeout(() => {
            if (this.composerEl?.nativeElement) {
                this.composerEl.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }, 0);
    }

    getLabelClass(lbl: string): { [key: string]: boolean } {
        const l = lbl.toLowerCase();
        return {
            'lbl-vip': l === 'vip',
            'lbl-late': l.includes('late'),
            'lbl-returning': l.includes('returning')
        };
    }

    archiveThread(): void {
        if (!this.conversationId) { return; }
        this.unifiedInboxService.archiveConversation(this.conversationId).subscribe({
            next: () => {
                this.archiveSuccess = true;
                this.archiveError = '';
                this.conversationArchived.emit(this.conversationId);
                setTimeout(() => { this.archiveSuccess = false; }, 3000);
            },
            error: () => {
                this.archiveError = 'Failed to archive conversation.';
                setTimeout(() => { this.archiveError = ''; }, 4000);
            }
        });
    }

    sendToBizDev(): void {
        if (!this.conversationId) { return; }
        this.bizDevSuccess = false;
        this.bizDevError = '';
        this.unifiedInboxService.sendToBizDev(this.conversationId).subscribe({
            next: () => {
                this.bizDevSuccess = true;
                setTimeout(() => { this.bizDevSuccess = false; }, 3000);
            },
            error: () => {
                this.bizDevError = 'Failed to forward to BizDev.';
                setTimeout(() => { this.bizDevError = ''; }, 4000);
            }
        });
    }

    openTapeChart(): void {
        const firstRes = (this.reservations || [])[0];
        if (firstRes && firstRes.checkInDate && firstRes.checkOutDate) {
            this.router.navigate(['/tape-chart'], {
                queryParams: {
                    checkIn: firstRes.checkInDate,
                    checkOut: firstRes.checkOutDate,
                    propertyCode: firstRes.propertyCode || ''
                }
            });
        } else {
            this.router.navigate(['/tape-chart']);
        }
    }

    toggleFlag(): void {
        this.isFlagged = !this.isFlagged;
        const label = 'Flagged';
        this.labelToggled.emit(label);
    }

    toggleOverflowMenu(): void {
        this.showOverflowMenu = !this.showOverflowMenu;
    }

    overflowMarkUnread(): void {
        this.showOverflowMenu = false;
        if (!this.conversationId) { return; }
        // Optimistic: mark conversation unread in parent via event
        this.conversationArchived.emit('unread:' + this.conversationId);
    }

    overflowMoveToStarred(): void {
        this.showOverflowMenu = false;
        if (!this.conversationId) { return; }
        this.labelToggled.emit('Starred');
    }

    overflowDelete(): void {
        this.showOverflowMenu = false;
        if (!this.conversationId) { return; }
        if (!confirm('Delete this conversation? This cannot be undone.')) { return; }
        this.deleteError = '';
        this.unifiedInboxService.archiveConversation(this.conversationId).subscribe({
            next: () => { this.conversationArchived.emit(this.conversationId); },
            error: () => {
                this.deleteError = 'Failed to delete conversation.';
                setTimeout(() => { this.deleteError = ''; }, 4000);
            }
        });
    }
}
